import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import CustomTextarea, { CustomInput } from '../Components/CustomFormElements';
import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import type {
  InformationPackageDetailApi,
  InformationPackageStatusApi,
  InformationPackageUnitPreviewApi,
} from '../Services/InformationPackageApiProviderTypes';

import Table from '../Components/Tables';
import { bytesToMB } from '../utils/bytesToMB';
import { statusLabel } from './InformationPackageHelpers';

interface InformationPackageDetailProps {
  canEdit?: boolean;
}

type InformationPackageDetailForm = Pick<
  InformationPackageDetailApi,
  'name' | 'description' | 'summary' | 'units'
>;

const filesColumns = [
  { name: 'Name', selector: (row: InformationPackageUnitPreviewApi) => row.name, sortable: false },
  {
    name: 'Size',
    selector: (row: InformationPackageUnitPreviewApi) => `${bytesToMB(row.size)} MB`,
    sortable: false,
  },
];

// The server refuses a reindex while the package is still waiting for the run
// it was last queued for, rather than start a second run over it.
const reindexFailureMessage = (error: unknown): string =>
  axios.isAxiosError(error) && error.response?.status === 409
    ? 'The information package is already queued for indexing.'
    : 'Failed to reindex the information package';

function InformationPackageDetailContent() {
  const navigate = useNavigate();
  const { organizationId, informationPackageId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getInformationPackageById, reindexInformationPackage } = useInformationPackageApi();

  const [loading, setLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [status, setStatus] = useState<InformationPackageStatusApi | null>(null);
  const [form, setForm] = useState<InformationPackageDetailForm>({
    name: '',
    description: '',
    summary: '',
    units: [],
  });

  useEffect(() => {
    const fetchInformationPackage = async () => {
      setLoading(true);

      try {
        const response = await getInformationPackageById(informationPackageId as string);

        setForm({
          name: response.name,
          description: response.description,
          summary: response.summary ?? '',
          units: response.units,
        });
        setStatus(response.status);
      } catch {
        showAlert('fail', 'Failed to load information package');
      } finally {
        setLoading(false);
      }
    };

    fetchInformationPackage();
  }, [getInformationPackageById, informationPackageId, showAlert]);

  const handleRowClick = (row: InformationPackageUnitPreviewApi) => {
    navigate(
      `/organizations/${organizationId}/information-package/${informationPackageId}/unit/${row.key}`,
    );
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/information-package`);
  };

  const onReindex = () => {
    showConfirmation(
      'Reindexing extracts, chunks, summarizes and analyzes every document of this package ' +
        'again, and replaces what was learned from them. It runs in the background and makes ' +
        'the AI calls again.',
      async () => {
        setReindexing(true);

        try {
          const response = await reindexInformationPackage(informationPackageId as string);
          setStatus(response.item.status);
          showAlert('success', 'Reindexing started. It runs in the background.');
        } catch (error) {
          showAlert('fail', reindexFailureMessage(error));
        } finally {
          setReindexing(false);
        }
      },
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name}</h2>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="information-package-name"
                label="Information Package Name"
                placeholder="Enter information package name"
                type="text"
                value={form.name}
                onChange={(id: string, value: string) => setForm({ ...form, name: value })}
                readOnly={true}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="information-package-status"
                label="Status"
                type="text"
                value={statusLabel(status)}
                readOnly={true}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="description"
                label="Description"
                name="description"
                addedText={form.description}
                onChange={(value: string) => setForm({ ...form, description: value })}
                resizable={false}
                readOnly={true}
                height="200px"
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="summary"
                label="Summary"
                name="summary"
                addedText={form.summary ?? ''}
                onChange={(value: string) => setForm({ ...form, summary: value })}
                resizable={false}
                readOnly={true}
                height="200px"
              />
            </div>
          </div>

          <div className="input-block d-flex flex-column justify-between">
            <h2 className="mb-4">Units</h2>
            <Table
              title=""
              columns={filesColumns}
              data={form.units}
              selectableRowsHighlight
              onRowClicked={handleRowClick}
              pagination={false}
            />
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button variant="secondary" size="medium" disabled={reindexing} onClick={onReindex}>
              Reindex
            </Button>
            <Button background="color2" size="medium" disabled={true}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InformationPackageDetail(_props: InformationPackageDetailProps) {
  return (
    <ConfirmationProvider>
      <InformationPackageApiProvider>
        <InformationPackageDetailContent />
      </InformationPackageApiProvider>
    </ConfirmationProvider>
  );
}

export default InformationPackageDetail;
